import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import PinDropIcon from '@material-ui/icons/PinDrop';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { ADDRESS_GRID_COLUMNS } from '../../constants/personAddressesTabConstants';
import PersonAddressLinkDialog from './PersonAddressLinkDialog';
import { useAddressDialog } from 'features/user/hooks/useAddressDialog';
import { getPeopleAddresses, deletePersonAddressLink } from '../../services/addressService';

const PersonAddressesTab = ({ personId, onPrimaryAddressChanged }) => {
  const dispatch = useDispatch();
  const { openAddressDialog } = useAddressDialog();
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const canEditPerson = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;
  const addressPerms = usePermissions(PERMISSION_RESOURCES.ADDRESS);
  const canViewAddressRecord = Boolean(addressPerms.Read || addressPerms.Edit);

  const loadAddresses = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleAddresses(personId),
      onSuccess: (data) => setAddresses(data),
    });
  }, [dispatch, personId]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const handleAdd = useCallback(() => {
    setEditingLink(null);
    setLinkFormOpen(true);
  }, []);

  const handleManagePrimary = useCallback((row) => {
    setEditingLink(row);
    setLinkFormOpen(true);
  }, []);

  const handleOpenAddressRecord = (row) =>
    openAddressDialog(row.ptsAddressID, {
      onAddressUpdated: handleAddressRecordSaved,
      onAddressDeleted: handleAddressRecordSaved,
    });

  const handleLinkFormClose = useCallback(() => {
    setLinkFormOpen(false);
    setEditingLink(null);
  }, []);

  const handleLinkSaved = useCallback(() => {
    handleLinkFormClose();
    loadAddresses();
    onPrimaryAddressChanged?.();
  }, [handleLinkFormClose, loadAddresses, onPrimaryAddressChanged]);

  const handleAddressRecordSaved = useCallback(() => {
    loadAddresses();
    onPrimaryAddressChanged?.();
  }, [loadAddresses, onPrimaryAddressChanged]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.FullAddressText || 'address'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () =>
              deletePersonAddressLink({ ptsPersonID: personId, ptsAddressID: row.ptsAddressID }),
            onSuccess: () => {
              dispatch(notify('Address deleted successfully', 'success'));
              loadAddresses();
              onPrimaryAddressChanged?.();
            },
          });
        },
        'Delete Address'
      );
    },
    [requestConfirm, dispatch, personId, loadAddresses, onPrimaryAddressChanged]
  );

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Address"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={addresses}
        columns={ADDRESS_GRID_COLUMNS}
        getRowId={(row) => row.ptsAddressID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Person Addresses' })}
        actions={{
          custom: [
            {
              icon: <EditIcon />,
              tooltip: 'Manage Primary',
              onClick: handleManagePrimary,
              disabled: !canEditPerson,
            },
            canViewAddressRecord && {
              icon: <PinDropIcon />,
              tooltip: 'Open Address Record',
              onClick: handleOpenAddressRecord,
              disabled: !canEditPerson,
            },
          ].filter(Boolean),
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {linkFormOpen && (
        <PersonAddressLinkDialog
          open={linkFormOpen}
          personId={personId}
          addressLink={editingLink}
          onClose={handleLinkFormClose}
          onSaved={handleLinkSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonAddressesTab;
